/**
 * Wishlist functionality - localStorage based
 * Supports add/remove products and display wishlist count
 */

(function() {
  'use strict';

  const STORAGE_KEY = 'zora_wishlist';
  const MAX_WISHLIST_ITEMS = 50;

  class Wishlist {
    constructor() {
      this.items = this.loadWishlist();
      this.init();
    }

    init() {
      this.arrangeProductButtons();

      // Bind all wishlist buttons on the page
      this.bindButtons();

      // Update all button states
      this.updateAllButtonStates();

      // Listen for dynamically added buttons
      this.observeMutations();

      // Dispatch initial count
      this.dispatchCountUpdate();
    }

    arrangeProductButtons(root = document) {
      const wrappers = root.matches && root.matches('.wishlist-button-wrapper')
        ? [root]
        : root.querySelectorAll('.wishlist-button-wrapper');

      wrappers.forEach((wrapper) => {
        const groupContent = wrapper.closest('.group-block-content');
        if (!groupContent) return;

        const siblings = Array.from(groupContent.children);
        const wrapperIndex = siblings.indexOf(wrapper);

        if (wrapperIndex === -1) return;

        let targetBlock = null;
        for (let i = wrapperIndex - 1; i >= 0; i -= 1) {
          if (siblings[i].matches('.buy-buttons-block')) {
            targetBlock = siblings[i];
            break;
          }
        }

        if (!targetBlock) return;

        const slot = targetBlock.querySelector('[data-wishlist-slot]');
        if (!slot || slot.contains(wrapper)) return;

        slot.hidden = false;
        slot.appendChild(wrapper);
        wrapper.classList.add('wishlist-button-wrapper--inline');
        targetBlock.classList.add('buy-buttons-block--has-wishlist');
      });
    }

    loadWishlist() {
      try {
        const data = localStorage.getItem(STORAGE_KEY);
        return data ? JSON.parse(data) : [];
      } catch (e) {
        console.warn('Could not load wishlist from localStorage');
        return [];
      }
    }

    saveWishlist() {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(this.items));
        this.dispatchCountUpdate();
      } catch (e) {
        console.warn('Could not save wishlist to localStorage');
      }
    }

    addItem(product) {
      // Check if already exists
      const existingIndex = this.items.findIndex(item => item.id === product.id);

      if (existingIndex === -1) {
        // Add to beginning of array
        this.items.unshift({
          id: product.id,
          title: product.title,
          handle: product.handle,
          image: product.image,
          price: product.price,
          addedAt: new Date().toISOString()
        });

        // Limit items
        if (this.items.length > MAX_WISHLIST_ITEMS) {
          this.items = this.items.slice(0, MAX_WISHLIST_ITEMS);
        }

        this.saveWishlist();
        this.updateAllButtonStates();
        this.showNotification(`${product.title} added to wishlist`);
        return true;
      }

      return false;
    }

    removeItem(productId) {
      const index = this.items.findIndex(item => item.id === productId);

      if (index > -1) {
        const removed = this.items[index];
        this.items.splice(index, 1);
        this.saveWishlist();
        this.updateAllButtonStates();
        this.showNotification(`${removed.title} removed from wishlist`);
        return true;
      }

      return false;
    }

    toggleItem(product) {
      const exists = this.items.some(item => item.id === product.id);

      if (exists) {
        return this.removeItem(product.id);
      } else {
        return this.addItem(product);
      }
    }

    isInWishlist(productId) {
      return this.items.some(item => item.id === productId);
    }

    getCount() {
      return this.items.length;
    }

    getItems() {
      return this.items;
    }

    clearWishlist() {
      this.items = [];
      this.saveWishlist();
      this.updateAllButtonStates();
    }

    bindButtons(root = document) {
      const buttons = root.matches && root.matches('[data-wishlist-add]')
        ? [root]
        : root.querySelectorAll('[data-wishlist-add]');

      buttons.forEach(button => {
        if (button.dataset.wishlistBound === 'true') return;
        button.addEventListener('click', (e) => this.handleButtonClick(e));
        button.dataset.wishlistBound = 'true';
      });
    }

    handleButtonClick(e) {
      e.preventDefault();
      const button = e.currentTarget;

      const product = {
        id: button.dataset.wishlistAdd,
        title: button.dataset.productTitle,
        handle: button.dataset.productHandle,
        image: button.dataset.productImage,
        price: button.dataset.productPrice
      };

      this.toggleItem(product);
    }

    updateAllButtonStates() {
      const buttons = document.querySelectorAll('[data-wishlist-add]');

      buttons.forEach(button => {
        const productId = button.dataset.wishlistAdd;
        const isInList = this.isInWishlist(productId);

        button.classList.toggle('is-active', isInList);
        button.setAttribute('aria-pressed', isInList ? 'true' : 'false');

        // Update button text if there's a text span
        const textSpan = button.querySelector('.wishlist-button__text');
        if (textSpan) {
          textSpan.textContent = isInList ? 'Added to Wishlist' : 'Add to Wishlist';
        }
      });

      // Update wishlist count badges if present
      const countBadges = document.querySelectorAll('[data-wishlist-count]');
      countBadges.forEach(badge => {
        badge.textContent = this.getCount();
        badge.style.display = this.getCount() > 0 ? '' : 'none';
      });
    }

    dispatchCountUpdate() {
      window.dispatchEvent(new CustomEvent('wishlist:updated', {
        detail: {
          count: this.getCount(),
          items: this.getItems()
        }
      }));
    }

    showNotification(message) {
      // Create notification element
      const notification = document.createElement('div');
      notification.className = 'wishlist-notification';
      notification.setAttribute('role', 'status');
      notification.setAttribute('aria-live', 'polite');
      notification.innerHTML = `
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
        </svg>
        <span>${message}</span>
      `;

      // Style the notification
      Object.assign(notification.style, {
        position: 'fixed',
        bottom: '80px',
        left: '50%',
        transform: 'translateX(-50%)',
        background: '#282262',
        color: '#fff',
        padding: '12px 20px',
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '14px',
        fontWeight: '500',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        zIndex: '9999',
        animation: 'wishlist-slide-up 0.3s ease'
      });

      // Add animation keyframes
      const style = document.createElement('style');
      style.textContent = `
        @keyframes wishlist-slide-up {
          from {
            opacity: 0;
            transform: translateX(-50%) translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateX(-50%) translateY(0);
          }
        }
      `;
      document.head.appendChild(style);

      // Add to page
      document.body.appendChild(notification);

      // Remove after delay
      setTimeout(() => {
        notification.style.opacity = '0';
        notification.style.transform = 'translateX(-50%) translateY(20px)';
        notification.style.transition = 'all 0.3s ease';

        setTimeout(() => {
          notification.remove();
        }, 300);
      }, 2500);
    }

    observeMutations() {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1) {
              this.arrangeProductButtons(node);

              // Check if node is or contains wishlist buttons
              const buttons = node.matches('[data-wishlist-add]')
                ? [node]
                : node.querySelectorAll('[data-wishlist-add]');

              if (buttons.length > 0) {
                this.bindButtons(node);
                buttons.forEach(button => {
                  const productId = button.dataset.wishlistAdd;
                  button.classList.toggle('is-active', this.isInWishlist(productId));
                });
              }
            }
          });
        });
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
    }
  }

  // Initialize wishlist
  const wishlist = new Wishlist();

  // Expose to global scope for external access
  window.ZoraWishlist = wishlist;

  // Shopify section load handler
  document.addEventListener('shopify:section:load', () => {
    wishlist.arrangeProductButtons();
    wishlist.bindButtons();
    wishlist.updateAllButtonStates();
  });
})();
